import React, { lazy, Suspense, useEffect, useState } from "react";
import { useOrg } from "@/lib/states/session/hooks";
import { errorToast } from "@/lib/utils/ToastUtils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import DialogAlertBlocker from "@/components/DialogAlertBlocker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc/client";

const TermsEditor = lazy(() => import("./TermsEditor"));

const DEFAULT_MARKDOWN = `## Terms and Conditions

Add the package terms clients must accept before subscribing.
`;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};


export default function PackageTermsDialog({ open, onOpenChange }: Props) {
  const org = useOrg();
  const dialogContentRef = React.useRef<HTMLDivElement | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [requiresAcceptance, setRequiresAcceptance] = useState<boolean | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const upsertTerms = trpc.packages.upsertTerms.useMutation();
  const utils = trpc.useUtils();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const {
    data: currentTerms,
    isLoading,
  } = trpc.packages.getCurrentTerms.useQuery(undefined, {
    enabled: open && Boolean(org?.id),
  });

  useEffect(() => {
    if (!open) {
      setMarkdown(null);
      setRequiresAcceptance(null);
      return;
    }

    if (isLoading || typeof currentTerms === "undefined") {
      return;
    }

    if (currentTerms) {
      setMarkdown(currentTerms.markdown);
      setRequiresAcceptance(currentTerms.requires_acceptance);
      return;
    }

    setMarkdown(DEFAULT_MARKDOWN);
    setRequiresAcceptance(false);
  }, [currentTerms, isLoading, open]);

  const handleSave = async () => {
    if (!org?.id) {
      return;
    }

    const trimmedMarkdown = (markdown ?? "").trim();
    if (!trimmedMarkdown) {
      toast.error("Terms content cannot be empty");
      return;
    }

    setIsSaving(true);

    try {
      const saved = await upsertTerms.mutateAsync({
        markdown: trimmedMarkdown,
        requiresAcceptance: resolvedRequiresAcceptance,
      });
      utils.packages.getCurrentTerms.setData(undefined, saved);
      toast.success("Terms updated");
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save package terms", error);
      errorToast(error as Error, "Failed to save package terms");
    } finally {
      setIsSaving(false);
    }
  };

  const orgName = org?.name?.trim() || "your organisation";

  const initialMarkdown = currentTerms?.markdown ?? DEFAULT_MARKDOWN;
  const initialRequiresAcceptance = currentTerms?.requires_acceptance ?? false;
  const resolvedMarkdown = markdown ?? DEFAULT_MARKDOWN;
  const resolvedRequiresAcceptance = requiresAcceptance ?? false;
  const hasUnsavedChanges =
    resolvedMarkdown.trim() !== initialMarkdown.trim() ||
    resolvedRequiresAcceptance !== initialRequiresAcceptance;

  const requestClose = () => {
    if (isSaving) {
      return;
    }

    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
      return;
    }

    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    requestClose();
  };

  return (
    <>
      <DialogAlertBlocker
        shouldBlock={showCloseConfirm}
        proceed={() => {
          setShowCloseConfirm(false);
          onOpenChange(false);
        }}
        cancel={() => setShowCloseConfirm(false)}
      />

      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        analyticsName="package_terms_dialog"
      >
        <DialogContent
          ref={dialogContentRef}
          className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden"
          onEscapeKeyDown={(event) => {
            if (hasUnsavedChanges || isSaving) {
              event.preventDefault();
              if (!isSaving) {
                setShowCloseConfirm(true);
              }
            }
          }}
          onInteractOutside={(event) => {
            if (hasUnsavedChanges || isSaving) {
              event.preventDefault();
              if (!isSaving) {
                setShowCloseConfirm(true);
              }
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Terms and conditions</DialogTitle>
          </DialogHeader>

          <div className="package-terms-dialog-body flex min-h-0 flex-1 flex-col space-y-6 overflow-hidden">
            <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-4 items-start">
              <div className="flex items-center gap-3 ">
                <Checkbox
                  id="requires-acceptance"
                  checked={resolvedRequiresAcceptance}
                  onCheckedChange={(checked) =>
                    setRequiresAcceptance(Boolean(checked))
                  }
                />
                <Label
                  htmlFor="requires-acceptance"
                  className="cursor-pointer text-sm font-medium"
                >
                  Show terms on package pages
                </Label>
              </div>
              <p className="text-muted-foreground text-sm">
                This will require users to accept these terms before subscribing
                to a package.
              </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col space-y-3 overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col space-y-3 overflow-hidden">
                <div className="mdxeditor-popup-host flex min-h-0 flex-1 overflow-hidden rounded-lg border">
                  {!open ||
                  isLoading ||
                  markdown === null ||
                  requiresAcceptance === null ? null : (
                    <Suspense fallback={null}>
                      <TermsEditor
                        termKey={currentTerms?.id ?? "new-package-terms"}
                        markdown={resolvedMarkdown}
                        onChange={setMarkdown}
                        overlayContainer={dialogContentRef.current}
                        orgName={orgName}
                      />
                    </Suspense>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
              disabled={isSaving}
            >
              Close
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
